import React from "react";

import SpeedrunEngine from "../speedrunEngine";

// Binding hook for SpeedrunEngine (multi-marc round). Constructs the
// engine, picks a fresh lineup when `active` flips true, and exposes a
// snapshot + advance/cycle methods to App. Resets on inactive so the
// next entry into speedrun re-rolls.
//
// Lineup picker:
//   - If `pinnedLineup` is non-empty (debug ?marcs=), use it as-is.
//   - Else shuffle `available` and take `roundSize`.

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickLineup({ available, pinnedLineup, roundSize }) {
  if (pinnedLineup && pinnedLineup.length > 0) return [...pinnedLineup];
  return shuffle(available).slice(0, roundSize);
}

export default function useSpeedrunEngine({
  active,
  available,
  pinnedLineup,
  roundSize,
}) {
  const engineRef = React.useRef(null);
  if (engineRef.current == null) {
    engineRef.current = new SpeedrunEngine();
  }

  const [snapshot, setSnapshot] = React.useState(() =>
    engineRef.current.getSnapshot(),
  );

  const recompute = React.useCallback(() => {
    setSnapshot(engineRef.current.getSnapshot());
  }, []);

  React.useEffect(() => {
    const engine = engineRef.current;
    const unsub = engine.subscribe(recompute);
    return () => {
      unsub();
      engine.dispose();
    };
  }, [recompute]);

  // Roll a fresh lineup on activation; reset on deactivation. Available
  // / pinned changes after start are intentionally ignored — the round
  // shape is locked at start time.
  React.useEffect(() => {
    if (active) {
      const lineup = pickLineup({ available, pinnedLineup, roundSize });
      engineRef.current.start({ lineup, available });
    } else {
      engineRef.current.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const advanceMarc = React.useCallback(() => {
    engineRef.current.advanceMarc();
  }, []);
  const cycleMarc = React.useCallback((delta) => {
    engineRef.current.cycleMarc(delta);
  }, []);
  const markStarted = React.useCallback(() => {
    engineRef.current.markStarted();
  }, []);
  // Roll a fresh lineup without leaving speedrun mode. Used by the
  // end-screen "Play Again" button — same path as the active=true
  // effect, just driven by an explicit user action.
  const restart = React.useCallback(() => {
    const lineup = pickLineup({ available, pinnedLineup, roundSize });
    engineRef.current.start({ lineup, available });
  }, [available, pinnedLineup, roundSize]);

  return { snapshot, advanceMarc, cycleMarc, markStarted, restart };
}
